import { useCallback, useMemo, useState } from "react";
import {
  contactEmailOptions,
  contactPreferredEmail,
  formatContactDisplayName,
  type Contact,
} from "@domaddress/shared";
import { api } from "../lib/api";
import EmailToRecipientsCopy from "./EmailToRecipientsCopy";
import PickContactEmailModal from "./PickContactEmailModal";

interface Props {
  groupId: number;
  members: Contact[];
  onClose: () => void;
}

function defaultSendEmail(m: Contact): string {
  return contactPreferredEmail(m) ?? contactEmailOptions(m)[0]?.email ?? "";
}

export default function ShareModal({ groupId, members, onClose }: Props) {
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  /** Per lid: gekozen adres voor het Aan-veld (na eventuele popup). */
  const [sendEmailByContactId, setSendEmailByContactId] = useState<Record<number, string>>({});
  const [emailPick, setEmailPick] = useState<{
    contact: Contact;
    options: ReturnType<typeof contactEmailOptions>;
  } | null>(null);
  const [recipients, setRecipients] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const addContact = useCallback((contact: Contact, sendEmail: string) => {
    setSelectedIds((prev) => (prev.includes(contact.id) ? prev : [...prev, contact.id]));
    setSendEmailByContactId((prev) => ({ ...prev, [contact.id]: sendEmail }));
  }, []);

  const removeContact = useCallback((id: number) => {
    setSelectedIds((prev) => prev.filter((x) => x !== id));
    setSendEmailByContactId((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  function toggleContact(id: number) {
    const m = members.find((x) => x.id === id);
    if (!m) return;

    if (selectedIds.includes(id)) {
      removeContact(id);
      return;
    }

    const options = contactEmailOptions(m);
    if (options.length > 1) {
      setEmailPick({ contact: m, options });
      return;
    }
    addContact(m, options[0]?.email ?? "");
  }

  function toggleAll() {
    if (selectedIds.length === members.length) {
      setSelectedIds([]);
      setSendEmailByContactId({});
      return;
    }
    const emails: Record<number, string> = {};
    for (const m of members) {
      emails[m.id] = defaultSendEmail(m);
    }
    setSelectedIds(members.map((m) => m.id));
    setSendEmailByContactId(emails);
  }

  const selectedMembers = useMemo(
    () => members.filter((m) => selectedIds.includes(m.id)),
    [members, selectedIds]
  );

  const emailToEntries = useMemo(
    () =>
      selectedMembers.map((c) => ({
        ...c,
        sendEmail: sendEmailByContactId[c.id] ?? defaultSendEmail(c),
      })),
    [selectedMembers, sendEmailByContactId]
  );

  async function handleSend() {
    setError("");
    const emails = recipients
      .split(/[\n,;]+/)
      .map((e) => e.trim())
      .filter(Boolean);

    if (selectedIds.length === 0) return setError("Kies minstens één contact");
    if (emails.length === 0) return setError("Vul minstens één ontvanger-e-mailadres in");

    setSending(true);
    try {
      const res = await api.post(`/api/groups/${groupId}/share`, {
        contact_ids: selectedIds,
        recipient_emails: emails,
        message: message || undefined,
      });
      setSuccess(res.data.message);
    } catch (err: unknown) {
      const ex = err as { response?: { data?: { error?: string } } };
      setError(ex.response?.data?.error ?? "Versturen mislukt");
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
          <div className="modal-header">
            <h2>Zend contacten</h2>
            <button type="button" className="modal-close" onClick={onClose} aria-label="Sluiten">
              ✕
            </button>
          </div>
          <div className="modal-body">
            {success ? (
              <div className="share-success">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="m9 12 2 2 4-4" />
                </svg>
                <p>{success}</p>
                <button type="button" className="btn-primary" onClick={onClose}>
                  Sluiten
                </button>
              </div>
            ) : (
              <>
                <div className="form-group">
                  <label>Kies contacten</label>
                  <p className="page-subtitle" style={{ marginTop: 0, marginBottom: "0.5rem" }}>
                    Vink de leden aan die u wilt delen. Bij meerdere e-mailadressen op een kaart kunt u kiezen welk
                    adres in het Aan-veld komt.
                  </p>
                  <div className="contact-picker">
                    <label className="contact-pick-item pick-all">
                      <input
                        type="checkbox"
                        checked={selectedIds.length === members.length && members.length > 0}
                        onChange={toggleAll}
                      />
                      <span className="pick-label">Alles selecteren ({members.length})</span>
                    </label>
                    {members.map((m) => {
                      const selected = selectedIds.includes(m.id);
                      const pickEmail = selected
                        ? sendEmailByContactId[m.id] ?? defaultSendEmail(m)
                        : defaultSendEmail(m);
                      return (
                        <label key={m.id} className="contact-pick-item">
                          <input type="checkbox" checked={selected} onChange={() => toggleContact(m.id)} />
                          <span className="pick-label">{formatContactDisplayName(m)}</span>
                          {pickEmail ? <span className="pick-email">{pickEmail}</span> : null}
                        </label>
                      );
                    })}
                  </div>
                </div>

                {selectedIds.length > 0 && (
                  <>
                    <p className="page-subtitle" style={{ marginTop: 0 }}>
                      {selectedIds.length} geselecteerde {selectedIds.length === 1 ? "contact" : "contacten"}.
                    </p>
                    <EmailToRecipientsCopy entries={emailToEntries} />
                  </>
                )}

                <hr
                  style={{
                    margin: selectedIds.length > 0 ? "1.25rem 0" : "0.5rem 0",
                    border: "none",
                    borderTop: "1px solid var(--surface-300, #e4dfe8)",
                  }}
                />

                <h3 className="section-title" style={{ marginTop: 0 }}>
                  Of: contacten per e-mail versturen (vCards)
                </h3>
                <p className="page-subtitle" style={{ marginTop: 0 }}>
                  Stuur .vcf-bijlagen naar één of meer ontvangers via de server. Dit is los van het Aan-veld hierboven
                  (kopiëren naar uw eigen e-mailprogramma).
                </p>

                <div className="form-group">
                  <label>Ontvanger(s) e-mail *</label>
                  <textarea
                    className="search-input"
                    style={{ width: "100%" }}
                    rows={3}
                    placeholder="voorbeeld@domein.nl, tweede@domein.nl"
                    value={recipients}
                    onChange={(e) => setRecipients(e.target.value)}
                  />
                  <small>Scheid meerdere adressen met komma&apos;s of een nieuwe regel</small>
                </div>

                <div className="form-group">
                  <label>Extra bericht (optioneel)</label>
                  <textarea
                    className="search-input"
                    style={{ width: "100%" }}
                    rows={2}
                    placeholder="Bijv. waarom stuur je deze gegevens…"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                  />
                </div>

                {error && <p className="form-error">{error}</p>}

                <div className="modal-footer">
                  <button type="button" className="btn-secondary" onClick={onClose}>
                    Annuleren
                  </button>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={() => void handleSend()}
                    disabled={sending || selectedIds.length === 0}
                  >
                    {sending ? "Bezig met verzenden…" : "vCards per e-mail versturen"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {emailPick && (
        <PickContactEmailModal
          contact={emailPick.contact}
          options={emailPick.options}
          onConfirm={(email) => {
            addContact(emailPick.contact, email);
            setEmailPick(null);
          }}
          onCancel={() => setEmailPick(null)}
        />
      )}
    </>
  );
}
